import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, ConfigProvider, message, Space, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import ru_RU from 'antd/locale/ru_RU';
import 'dayjs/locale/ru';
import { useWindowWidth } from '@react-hook/window-size';
import Header from './components/header/header';
import NavMenu from './components/nav-menu/nav-menu';
import Home from './components/home/home';
import Catalog from './components/catalog/catalog';
import Purchases from './components/purchase/purchases';
import Visits from './components/visits/visits';
import Calendar from './components/calendar/calendar';
import { companyLoaded, userLoaded } from './redux/actions';
import { getIsVkSession, setStyleProperty, hasNocookiesOptOut } from './extensions/utils';
import { serverFetch } from './server';
import { Footer } from 'antd/lib/layout/layout';
import CookieConsent, {getCookieConsentValue} from "react-cookie-consent";
import History from './components/history/history';
import Article from './components/article/article';
import Vk from './components/auth/vk';
import AccountLock from './components/account-lock/account-lock';
import { Sale } from './components';
import CloudPayments from './components/widgets/cloudpayments';
import Sberpay from './components/widgets/sberpay';
import PwaInstallPrompt from './components/pwa-install-prompt/pwa-install-prompt';
import Action from './components/action/action';
import Cart from './components/cart/cart';
import OfflineScreen from './components/offline-screen/offline-screen';
import AppPaymentReturn from './components/app-payment-return/app-payment-return';

const shouldShowCatalogBackground = (pathname) =>
    pathname === '/' ||
    pathname === '/home' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/calendar') ||
    pathname.startsWith('/purchases') ||
    pathname.startsWith('/history');

const App = () => {
    const { Content } = Layout;
    const d = useDispatch();
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const onlyWidth = useWindowWidth();
    const isFrame = window.self !== window.top;
    const location = useLocation();
    const [online, setOnline] = useState(
        () => typeof navigator !== 'undefined' && navigator.onLine
    );
    useEffect(() => {
        const onOnline = () => setOnline(true);
        const onOffline = () => setOnline(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const ptag = searchParams.get(process.env.REACT_APP_UTM_TAG_NAME);
        if (ptag !== sessionStorage.getItem(process.env.REACT_APP_UTM_TAG_NAME)) sessionStorage.setItem(process.env.REACT_APP_UTM_TAG_NAME, ptag);
        const paramValue = searchParams.get(process.env.REACT_APP_VK_PARAM_NAME);
        const sess = sessionStorage.getItem(process.env.REACT_APP_VK_PARAM_NAME);
        if (paramValue && (!sess || paramValue !== sess)) {
            // Проверить - если пользователь не авторизован а есть флаг авторизации то пытаемся
            if(!user) {
                sessionStorage.setItem(process.env.REACT_APP_VK_PARAM_NAME, paramValue);
                const bodyData = JSON.parse(atob(paramValue));
                //message.info("bodyData");
                if (bodyData && bodyData.vkauth) {
                    // Авторизуем пользователя
                    serverFetch(`account/signinvk`, { method: 'POST', bodyData })
                    .then(data => d(userLoaded(data)))
                    .catch(() => message.error('Ошибка входа. Попробуйте перегрузить страницу и предоставить доступ приложению до данных почты и телефона'));
                }
            }
            else { d(userLoaded(null)); }
        }
    }, [location.search, user]);
    
    useEffect(() => {
        serverFetch(`company`, { method: 'GET' })
            .then((data) => {
                if (data) {
                    d(companyLoaded(data));
                    setStyleProperty('--default-color', data.primaryColor);
                    setStyleProperty('--primary-color', data.primaryColor);
                    setStyleProperty('--hover-color', data.primaryColor);
                    setStyleProperty('--active-color', data.primaryColor);
                    document.title = data.name;
                    // Устанавливаем favicon из pwaImage, если он есть
                    if (data.pwaImage) {
                        // Функция для обновления или создания link элемента
                        const updateOrCreateLink = (rel, href) => {
                            let link = document.querySelector(`link[rel='${rel}']`);
                            if (!link) {
                                link = document.createElement('link');
                                link.rel = rel;
                                document.getElementsByTagName('head')[0].appendChild(link);
                            }
                            link.href = href;
                            // Определяем тип по расширению файла
                            if (href.toLowerCase().endsWith('.png')) link.type = 'image/png';
                            else if (href.toLowerCase().endsWith('.jpg') || href.toLowerCase().endsWith('.jpeg')) link.type = 'image/jpeg';
                            else if (href.toLowerCase().endsWith('.svg')) link.type = 'image/svg+xml';
                        };

                        // Обновляем все варианты favicon
                        updateOrCreateLink('icon', data.pwaImage);
                        updateOrCreateLink('shortcut icon', data.pwaImage);
                        
                        // Обновляем Apple Touch Icons для всех размеров (iOS/Safari)
                        const appleTouchSizes = ['', '152x152', '180x180', '167x167'];
                        appleTouchSizes.forEach(size => {
                            let appleLink = size 
                                ? document.querySelector(`link[rel='apple-touch-icon'][sizes='${size}']`)
                                : document.querySelector(`link[rel='apple-touch-icon']:not([sizes])`);
                            
                            if (!appleLink) {
                                appleLink = document.createElement('link');
                                appleLink.rel = 'apple-touch-icon';
                                if (size) appleLink.sizes = size;
                                document.getElementsByTagName('head')[0].appendChild(appleLink);
                            }
                            appleLink.href = data.pwaImage;
                        });

                        // Обновляем Apple-специфичные мета-теги для Safari
                        const updateOrCreateMeta = (name, content) => {
                            let meta = document.querySelector(`meta[name='${name}']`);
                            if (!meta) {
                                meta = document.createElement('meta');
                                meta.name = name;
                                document.getElementsByTagName('head')[0].appendChild(meta);
                            }
                            meta.content = content;
                        };

                        updateOrCreateMeta('apple-mobile-web-app-title', data.name);
                        updateOrCreateMeta('application-name', data.name);
                        
                        // Обновляем description если есть
                        if (data.description) {
                            updateOrCreateMeta('description', data.description);
                        }

                        // Для браузеров с поддержкой manifest (Chrome, Edge, Firefox) обновляем manifest.json динамически
                        // Safari игнорирует manifest.json, поэтому для него используем Apple мета-теги выше
                        const manifestLink = document.querySelector("link[rel='manifest']");
                        if (manifestLink && data.name) {
                            // Определяем тип изображения для manifest
                            let imageType = 'image/png';
                            const imgUrl = data.pwaImage.toLowerCase();
                            if (imgUrl.endsWith('.jpg') || imgUrl.endsWith('.jpeg')) imageType = 'image/jpeg';
                            else if (imgUrl.endsWith('.svg')) imageType = 'image/svg+xml';
                            else if (imgUrl.endsWith('.webp')) imageType = 'image/webp';

                            // Создаем динамический manifest с правильными иконками
                            const manifest = {
                                short_name: data.name,
                                name: data.name,
                                description: "Онлайн бронирование и покупка абонементов",
                                icons: [
                                    { src: data.pwaImage, sizes: "192x192", type: imageType, purpose: "any maskable" },
                                    { src: data.pwaImage, sizes: "512x512", type: imageType, purpose: "any maskable" },
                                    { src: data.pwaImage, sizes: "152x152", type: imageType },
                                    { src: data.pwaImage, sizes: "180x180", type: imageType },
                                    { src: data.pwaImage, sizes: "167x167", type: imageType }
                                ],
                                start_url: ".",
                                scope: ".",
                                id: "/",
                                display: "standalone",
                                orientation: "portrait-primary",
                                theme_color: data.primaryColor || "#000000",
                                background_color: "#ffffff",
                                categories: ["sports", "fitness", "booking"],
                                lang: "ru",
                                prefer_related_applications: false
                            };
                            const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
                            const manifestURL = URL.createObjectURL(manifestBlob);
                            manifestLink.href = manifestURL;
                        }

                        // Обновляем theme-color
                        if (data.primaryColor) {
                            let themeColorMeta = document.querySelector("meta[name='theme-color']");
                            if (themeColorMeta) {
                                themeColorMeta.content = data.primaryColor;
                            }
                            
                            // Обновляем Apple status bar style color
                            let appleStatusBarMeta = document.querySelector("meta[name='apple-mobile-web-app-status-bar-style']");
                            if (appleStatusBarMeta) {
                                appleStatusBarMeta.content = 'default';
                            }
                        }
                    }

                    if (window.self === window.top && company.chat && (getCookieConsentValue() || hasNocookiesOptOut())) {
                        var script1 = document.createElement('script'); 
	                    script1.src = process.env.REACT_APP_CHAT;
	                    document.body.appendChild(script1);
                    }
                } else {
                    message.error('Ошибка загрузки компании');
                }
            })
            .catch((e) => { e.UserMessage && message.error(e.UserMessage); });
    }, []);

    useEffect(() => {
        if (user != null) {
            let queryParams = {
                sessionId: user.id,
            };
            serverFetch('account/checksession', { method: 'GET', queryParams: queryParams }, null)
                .then(() => { })
                .catch(() => { d(userLoaded()) });
        }
    }, [user]);

    useEffect(() => {
        const image = company?.catalogBackgroundImage;
        setStyleProperty(
            '--block-content-backround-img',
            image && shouldShowCatalogBackground(location.pathname) ? `url("${image}")` : ''
        );
    }, [company, location.pathname]);

    const linkStyle = { cursor: 'pointer', color: '#595959' };
    const offerAutopay = company && company.offerAutopay ? company.offerAutopay : process.env.REACT_APP_OFFER_AUTOPAY;
    const offerPayment = company && company.offerPayment ? company.offerPayment : process.env.REACT_APP_OFFER_PAYMENT;

    if (!online) {
        return <OfflineScreen />;
    }

    return (
        company && !company.isActive ? <AccountLock />
        : <ConfigProvider locale={ru_RU}>
            <Layout style={{ minHeight: '100vh' }}>
                <div className='app-topbar'>
                    <div className='header-container app-topbar-inner'>
                        {isFrame || <Header />}
                        <NavMenu />
                    </div>
                </div>
                <Content>
                    <div className='content-container'>
                        <Switch>
                            <Route exact path='/' component={Home} />
                            <Route path='/home' component={Home} />
                            <Route path='/catalog/:id?' component={Catalog} />
                            <Route path='/cart' component={Cart} />
                            <Route path='/app/payment/:status' component={AppPaymentReturn} />
                            <Route path='/purchases' component={Purchases} />
                            <Route path='/visits' component={Visits} />
                            <Route path='/calendar' component={Calendar} />
                            <Route path='/history' component={History} />
                            <Route path='/article/:name' component={Article} />
                            <Route path='/action/:id' component={Action} />
                            <Route path='/vk' component={Vk} />
                            <Route path='/sale/:code?' component={Sale} />
                            <Route path='/widgets/cloudpayments' component={CloudPayments} />
                            <Route path='/widgets/sberpay' component={Sberpay} />
                            <Redirect to="/" />
                        </Switch>
                        <div id="main-footer" style={{height: 1}}/>
                    </div>
                </Content>
                {(!isFrame || getIsVkSession()) && !hasNocookiesOptOut() ? <Footer className='app-footer'>
                    <Space size={onlyWidth >= 744 ? 'middle' : 'small'} direction={onlyWidth >= 744 ? 'horizontal' : 'vertical'} style={{ fontSize: 14, width: '100%', justifyContent: 'center' }} >
                        <a href='https://sport24.cloud/' target='_blank' rel='noreferrer'><div style={linkStyle}>(с) Виджет онлайн продаж</div></a>
                        <a href={offerPayment}><div style={linkStyle}>Описание сервиса</div></a>
                        <a href="/article/agreement"><div style={linkStyle}>Пользовательское соглашение</div></a>
                        <a href="/article/rules"><div style={linkStyle}>Условия и правила</div></a>
                        <a href={offerAutopay}><div style={linkStyle}>Условия сервиса "Автоплатеж"</div></a>
                        <PwaInstallPrompt />
                    </Space>                        
                </Footer> : null}
                {!isFrame && !hasNocookiesOptOut() && (
                    <CookieConsent buttonText="Принять" onAccept={() => window.location.reload()}>
                        Мы используем файлы cookie чтобы обеспечить Вам наилучшую работу на нашем веб-сайте. Продолжая использовать этот сайт, Вы даете согласие на использование файлов cookie
                    </CookieConsent>
                )}
            </Layout>
        </ConfigProvider>
    );
};

export default App;
